import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnalyticsOverview } from "@/hooks/use-analytics";
import { useBrandContext } from "@/contexts/brand-context";
import { useLocale, formatNumber } from "@/i18n/format";

export function AnalyticsWidget() {
  const { t } = useTranslation("dashboard");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const { data, isLoading } = useAnalyticsOverview(brand?.id ?? "", 7);

  if (!brand || isLoading || !data) return null;
  if (data?.addon && data.addon.available === false) return null;

  const impressions = data.kpi?.impressions;
  if (!impressions) return null;

  const currentNum = Number(impressions.current);
  const delta = impressions.deltaPct as number | null;
  const positive = typeof delta === "number" && delta > 0;
  const negative = typeof delta === "number" && delta < 0;
  const DeltaIcon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const deltaCls = positive
    ? "text-emerald-600"
    : negative
      ? "text-red-600"
      : "text-muted-foreground";
  const deltaLabel =
    delta === null
      ? "—"
      : `${positive ? "▲" : negative ? "▼" : ""} ${Math.abs(delta).toFixed(1)}%`;

  return (
    <Link
      to={`/${brand.slug}/analytics`}
      className="block rounded-lg border bg-card p-4 transition hover:bg-muted/40"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t("analyticsWidget.title")}</h3>
        <span className="text-xs text-muted-foreground">{t("analyticsWidget.viewAnalytics")}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold">
        {Number.isFinite(currentNum) ? formatNumber(currentNum, locale) : String(impressions.current)}
      </div>
      <div className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${deltaCls}`}>
        <DeltaIcon className="h-3 w-3" />
        {deltaLabel}
        <span className="ml-1 text-muted-foreground">{t("analyticsWidget.vsPreviousPeriod")}</span>
      </div>
    </Link>
  );
}
